import { resolveJobDocumentDownloadUrl } from '@/domains/jobs/api/jobDocumentsApi';
import { truncateFileName } from '@/shared/lib/truncateFileName';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import type { RawCommunicationLogRow } from '@skewb-works/contracts';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { EVENT_LABELS, extractActor, extractMessage, getIcon } from './ActivityLogDocumentsTabs';

interface ActivityLogItemProps {
  row: RawCommunicationLogRow;
  isLast: boolean;
  jobId: number;
}

function AttachmentLink({
  att,
  jobId,
}: {
  att: { fileName: string; S3Key: string };
  jobId: number;
}) {
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    setLoading(true);
    try {
      const { url } = await resolveJobDocumentDownloadUrl(jobId, att.S3Key, 'inline');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      showErrorToast('Unable to open document', 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleOpen}
      disabled={loading}
      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
    >
      <ExternalLink className="w-3 h-3 flex-shrink-0" />
      {loading ? 'Opening…' : truncateFileName(att.fileName)}
    </button>
  );
}

export function ActivityLogItem({ row, isLast, jobId }: ActivityLogItemProps) {
  const { icon: Icon, color, bgColor } = getIcon(row.eventType);
  const label =
    EVENT_LABELS[row.eventType] ?? row.eventType.replace('work_order.', '').replace(/_/g, ' ');
  const description = extractMessage(row.payload, row.eventType);
  const actor = extractActor(row.payload, row.direction, row.actor);
  const showDescription = description !== label;

  // Render attachments for any event whose payload carries them — reject,
  // more-info (both directions) and document-upload all ship
  // workOrder.attachments. Previously this was gated to more-info only, so
  // files attached when rejecting a job never showed in the log (WRK-5639).
  const attachments: Array<{ fileName: string; S3Key: string }> = (
    ((row.payload as any)?.workOrder?.attachments ?? []) as any[]
  )
    .map((a) => ({
      fileName: a?.fileName,
      S3Key: a?.S3Key ?? a?.s3Key,
    }))
    .filter((a) => a.fileName && a.S3Key);

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div
          className={`w-10 h-10 rounded-full ${bgColor} flex items-center justify-center flex-shrink-0`}
        >
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        {!isLast && <div className="w-0.5 flex-1 min-h-[24px] bg-border mt-2" />}
      </div>

      <div className="flex-1 pb-3">
        <div className="font-semibold text-foreground text-sm flex items-center gap-2">
          <span>{label}</span>
          {[
            'work_order.engineer_en_route',
            'work_order.engineer_on_site',
            'work_order.engineering_complete',
          ].includes(row.eventType) &&
            (() => {
              const engineers = (row.payload as any)?.workOrder?.assignedEngineers;
              const engineerId =
                Array.isArray(engineers) && engineers.length > 0 ? engineers[0]?.id : null;
              if (!engineerId) return null;
              return (
                <a
                  href={`/users/${engineerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  View Engineer Profile
                  <ExternalLink className="w-3 h-3" />
                </a>
              );
            })()}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          by {actor} •{' '}
          {new Date(row.createdTime).toLocaleString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        {showDescription && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {attachments.map((att, i) => (
              <AttachmentLink key={i} att={att} jobId={jobId} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
